import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';

import {
  absoluteAssetUrl,
  absoluteUrl,
  buildArticleSeo,
  SEO_BY_KEY,
  SEO_DEFAULT_IMAGE,
  SEO_SITE_NAME,
} from '../seo/seo.config';
import type { SeoMeta } from '../seo/seo.types';

const JSON_LD_ID = 'skilled-seo-jsonld';
const CANONICAL_ID = 'skilled-seo-canonical';

@Injectable({ providedIn: 'root' })
export class SeoService {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly document = inject(DOCUMENT);
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly router = inject(Router);

  init(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.applyForCurrentRoute();

    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.applyForCurrentRoute());
  }

  update(meta: SeoMeta): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    this.title.setTitle(meta.title);
    this.setMetaTag('name', 'description', meta.description);
    this.setMetaTag('name', 'robots', meta.robots ?? 'index, follow');

    const image = absoluteAssetUrl(meta.image ?? SEO_DEFAULT_IMAGE);
    const url = absoluteUrl(meta.path);
    const type = meta.type ?? 'website';

    this.setMetaTag('property', 'og:title', meta.title);
    this.setMetaTag('property', 'og:description', meta.description);
    this.setMetaTag('property', 'og:url', url);
    this.setMetaTag('property', 'og:type', type);
    this.setMetaTag('property', 'og:image', image);
    this.setMetaTag('property', 'og:site_name', SEO_SITE_NAME);

    this.setMetaTag('name', 'twitter:card', 'summary_large_image');
    this.setMetaTag('name', 'twitter:title', meta.title);
    this.setMetaTag('name', 'twitter:description', meta.description);
    this.setMetaTag('name', 'twitter:image', image);

    this.setCanonical(url);
    this.setJsonLd(meta.jsonLd ?? []);
  }

  private applyForCurrentRoute(): void {
    let route = this.router.routerState.root;
    while (route.firstChild) {
      route = route.firstChild;
    }

    const seoKey = route.snapshot.data['seoKey'] as string | undefined;
    const slug = route.snapshot.paramMap.get('slug');

    if (seoKey === 'article' && slug) {
      const articleSeo = buildArticleSeo(slug);
      if (articleSeo) {
        this.update(articleSeo);
        return;
      }
    }

    const config = seoKey ? SEO_BY_KEY[seoKey] : SEO_BY_KEY['home'];
    if (config) {
      this.update(config);
    }
  }

  private setMetaTag(attrSelector: 'name' | 'property', attrName: string, content: string): void {
    this.meta.updateTag({ [attrSelector]: attrName, content });
  }

  private setCanonical(url: string): void {
    const head = this.document.head;
    let link = this.document.getElementById(CANONICAL_ID) as HTMLLinkElement | null;

    if (!link) {
      link = this.document.createElement('link');
      link.id = CANONICAL_ID;
      link.rel = 'canonical';
      head.appendChild(link);
    }

    link.href = url;
  }

  private setJsonLd(blocks: Record<string, unknown>[]): void {
    const head = this.document.head;
    this.document.getElementById(JSON_LD_ID)?.remove();

    if (!blocks.length) {
      return;
    }

    const script = this.document.createElement('script');
    script.id = JSON_LD_ID;
    script.type = 'application/ld+json';
    script.text = JSON.stringify(blocks.length === 1 ? blocks[0] : blocks);
    head.appendChild(script);
  }
}
